import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/brand";
import Seo from "@/components/Seo";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) nav("/admin"); }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      nav("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#fdfbf7]">
      <Seo title="Admin Login | Legacy India Travel" />
      {/* Visual */}
      <div className="relative hidden lg:block">
        <img src="https://images.pexels.com/photos/16497359/pexels-photo-16497359.jpeg?auto=compress&cs=tinysrgb&w=1400" alt="India" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2d1616] via-[#2d1616]/50 to-[#7c1d1d]/40" />
        <div className="absolute inset-0 p-12 flex flex-col justify-end text-white">
          <div className="font-serif text-4xl">Legacy <span className="italic text-[#e8c970]">India Travel</span></div>
          <p className="mt-3 text-white/80 max-w-sm">Content Management System — manage tours, destinations, blog, enquiries and more.</p>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} data-testid="admin-login-form" className="w-full max-w-sm">
          <img src={LOGO_URL} alt="Legacy India Travel" className="h-24 w-auto object-contain mb-6" />
          <h1 className="font-serif text-4xl text-[#3b1a1a]">Admin Login</h1>
          <p className="text-[#5a4b4b] mt-2 text-sm">Sign in to manage your website.</p>

          {error && <div data-testid="login-error" className="mt-5 bg-[#7c1d1d]/10 border border-[#7c1d1d]/30 text-[#7c1d1d] text-sm px-4 py-3">{error}</div>}

          <div className="mt-6 space-y-4">
            <label className="block"><span className="block text-[10px] tracking-[0.2em] uppercase text-[#5a4b4b] mb-1.5">Email</span>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="inp" placeholder="admin@legacyindiatravel.com" /></label>
            <label className="block"><span className="block text-[10px] tracking-[0.2em] uppercase text-[#5a4b4b] mb-1.5">Password</span>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="inp" placeholder="••••••••" /></label>
          </div>

          <button type="submit" disabled={loading} data-testid="login-submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#7c1d1d] hover:bg-[#5a1515] disabled:opacity-60 text-white py-4 text-xs tracking-[0.24em] uppercase transition-colors">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Sign In
          </button>
        </form>
      </div>
    </div>
  );
}
